import { supabase } from './supabase';

export const adminService = {
  async logAction(
    adminId: string,
    actionType: string,
    targetType: string,
    targetId: string | null,
    details: Record<string, any> = {}
  ) {
    const { error } = await supabase.from('admin_actions').insert({
      admin_id: adminId,
      action_type: actionType,
      target_type: targetType,
      target_id: targetId,
      details,
    });

    if (error) {
      console.error('Error logging admin action:', error);
    }
  },

  async getAllUsers(filters: {
    role?: string;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}) {
    let query = supabase
      .from('profiles')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.role && filters.role !== 'all') {
      query = query.eq('role', filters.role);
    }

    if (filters.search) {
      query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
    }

    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    if (filters.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    return await query;
  },

  async getUserWithDetails(userId: string) {
    const [profile, credits, subscriptions, applications, jobs] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
      supabase.from('credits').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('subscriptions').select('*').eq('user_id', userId).eq('status', 'active'),
      supabase.from('applications').select('*, job:job_id(*)').eq('applicant_id', userId),
      supabase.from('jobs').select('*, applications(count)').eq('employer_id', userId),
    ]);

    return {
      profile: profile.data,
      credits: credits.data,
      subscriptions: subscriptions.data,
      applications: applications.data,
      jobs: jobs.data,
    };
  },

  async adjustUserCredits(
    adminId: string,
    userId: string,
    amount: number,
    reason: string
  ) {
    const { data: credits } = await supabase
      .from('credits')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (!credits) {
      throw new Error('User credits not found');
    }

    const newBalance = credits.balance + amount;

    const { error: updateError } = await supabase
      .from('credits')
      .update({
        balance: newBalance,
        total_earned: amount > 0 ? credits.total_earned + amount : credits.total_earned,
        total_spent: amount < 0 ? credits.total_spent + Math.abs(amount) : credits.total_spent,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId);

    if (updateError) throw updateError;

    await supabase.from('credit_transactions').insert({
      user_id: userId,
      amount: amount,
      type: 'admin_adjustment',
      description: reason,
    });

    await this.logAction(adminId, 'credit_adjustment', 'user', userId, {
      amount,
      reason,
      new_balance: newBalance,
    });

    return { success: true, newBalance };
  },

  async updateUserSubscription(
    adminId: string,
    userId: string,
    subscriptionData: {
      plan_type: string;
      status: string;
      price?: number;
    }
  ) {
    const { data: existingSub } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .maybeSingle();

    if (existingSub) {
      const { error } = await supabase
        .from('subscriptions')
        .update({
          ...subscriptionData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingSub.id);

      if (error) throw error;
    } else {
      const now = new Date();
      const endDate = new Date(now);
      endDate.setMonth(endDate.getMonth() + 1);

      const { error } = await supabase.from('subscriptions').insert({
        user_id: userId,
        plan_type: subscriptionData.plan_type,
        status: subscriptionData.status,
        price: subscriptionData.price || 0,
        current_period_start: now.toISOString(),
        current_period_end: endDate.toISOString(),
        next_billing_date: endDate.toISOString(),
      });

      if (error) throw error;
    }

    await this.logAction(adminId, 'subscription_update', 'user', userId, subscriptionData);

    return { success: true };
  },

  async suspendUser(adminId: string, userId: string, reason: string) {
    const { error } = await supabase
      .from('profiles')
      .update({ bio: `[SUSPENDED] ${reason}` })
      .eq('id', userId);

    if (error) throw error;

    await this.logAction(adminId, 'user_suspended', 'user', userId, { reason });

    return { success: true };
  },

  async getPlatformMetrics() {
    const today = new Date().toISOString().split('T')[0];

    const { data: metrics } = await supabase
      .from('platform_metrics')
      .select('*')
      .eq('metric_date', today)
      .maybeSingle();

    if (metrics) {
      return metrics;
    }

    const [users, jobSeekers, employers, jobs, applications, revenue] = await Promise.all([
      supabase.from('profiles').select('*', { count: 'exact', head: true }),
      supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'job_seeker'),
      supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'employer'),
      supabase.from('jobs').select('*', { count: 'exact', head: true }).eq('status', 'active'),
      supabase.from('applications').select('*', { count: 'exact', head: true }),
      supabase.from('subscriptions').select('price').eq('status', 'active'),
    ]);

    const totalRevenue = revenue.data?.reduce((sum, sub) => sum + Number(sub.price), 0) || 0;

    return {
      metric_date: today,
      total_users: users.count || 0,
      total_job_seekers: jobSeekers.count || 0,
      total_employers: employers.count || 0,
      total_jobs_active: jobs.count || 0,
      total_applications: applications.count || 0,
      total_revenue: totalRevenue,
    };
  },

  async getRecentActivity(limit: number = 50) {
    const { data, error } = await supabase
      .from('admin_actions')
      .select('*, admin:admin_id(full_name, email)')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  async getAllJobs(filters: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}) {
    let query = supabase
      .from('jobs')
      .select('*, employer:employer_id(full_name, email, company_name), applications(count)', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,company.ilike.%${filters.search}%`);
    }

    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    if (filters.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    return await query;
  },

  async updateJobStatus(adminId: string, jobId: string, status: string) {
    const { error } = await supabase
      .from('jobs')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', jobId);

    if (error) throw error;

    await this.logAction(adminId, 'job_status_update', 'job', jobId, { status });

    return { success: true };
  },

  async getAllTransactions(filters: {
    userId?: string;
    type?: string;
    limit?: number;
    offset?: number;
  } = {}) {
    let query = supabase
      .from('credit_transactions')
      .select('*, user:user_id(full_name, email, role)', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }

    if (filters.type && filters.type !== 'all') {
      query = query.eq('type', filters.type);
    }

    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    if (filters.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    return await query;
  },

  async getFinancialMetrics() {
    const [subscriptions, purchases] = await Promise.all([
      supabase.from('subscriptions').select('plan_type, price, status'),
      supabase.from('purchases').select('product_type, amount, status').eq('status', 'completed'),
      supabase.from('credit_transactions').select('amount, type').gt('amount', 0),
    ]);

    const subscriptionRevenue = subscriptions.data
      ?.filter(s => s.status === 'active')
      .reduce((sum, sub) => sum + Number(sub.price), 0) || 0;

    const purchaseRevenue = purchases.data?.reduce((sum, p) => sum + Number(p.amount), 0) || 0;

    const totalRevenue = subscriptionRevenue + purchaseRevenue;

    return {
      subscriptionRevenue,
      purchaseRevenue,
      totalRevenue,
      activeSubscriptions: subscriptions.data?.filter(s => s.status === 'active').length || 0,
      totalPurchases: purchases.data?.length || 0,
    };
  },

  async getCohortData() {
    const { data, error } = await supabase
      .from('cohort_analysis')
      .select('*')
      .order('cohort_date', { ascending: false })
      .limit(12);

    if (error) throw error;
    return data || [];
  },

  async getFeatureFlags() {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async updateFeatureFlag(
    adminId: string,
    flagId: string,
    updates: { is_enabled?: boolean; rollout_percentage?: number }
  ) {
    const { error } = await supabase
      .from('feature_flags')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', flagId);

    if (error) throw error;

    await this.logAction(adminId, 'feature_flag_update', 'feature_flag', flagId, updates);

    return { success: true };
  },
};
